/** Copyright (c) 2019 T.Rowe Price. All rights reserved. */

import './TrustedContactsDeleteGlobal.scss';

import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import { string } from 'prop-types';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';

import AccountDisplay from 'components/shared/AccountDisplay/AccountDisplay';
import BaseModal from 'components/shared/Modal/BaseModal';
import { getCustomerTrustedContacts } from 'state/customer/CustomerSelectors';
import LinkButton from 'components/shared/Buttons/LinkButton';
import ModalLoadingSpinner from 'components/shared/Modal/ModalLoadingSpinner';
import { getNonNullTrustedContactMappedGroups } from 'state/preferences/PreferencesSelectors';
import { getView } from 'state/view/ViewSelectors';
import {
  getClientMissions,
  updateCustomerPreferences
} from 'services/Rest';
import { TrustedContactsButtonWithPermissions } from './TrustedContactsModal';
import { errorUpdateTrustedContacts } from 'state/preferences/PreferencesActions';
import Store from 'state/Store';
import { clientMissionsEnabled, isTrustedContactsAccountNumberEnabled } from 'services/FeatureFlags';
import { getAccountsById } from 'state/account/AccountSelectors';
import { applyAccountNumbersToContacts } from 'services/Preferences';

const GroupHeader = ({ account }) => {
  const { groupingClassName, name } = account;

  return (
    <div
      className={`trusted-contacts-delete-global-group-header-container ${groupingClassName}`}>
      <AccountDisplay account={name} withType={true} />
    </div>
  );
};

const GroupRow = ({ account, onChange, trustedContactsState }) => (
  <div className="trusted-contacts-delete-global-row">
    <HasTrustedContacts
      account={account}
      onChange={onChange}
      trustedContactsState={trustedContactsState}
    />
  </div>
);

const HasTrustedContacts = ({ account, onChange, trustedContactsState }) => {
  const { trustedContacts = [] } = account;
  return trustedContacts.map((contact, key) => (
    <TrustedContact
      account={account}
      contact={contact}
      key={key}
      onChange={onChange}
      trustedContactsState={trustedContactsState}
    />
  ));
};

const TrustedContact = ({
                          account,
                          contact,
                          onChange,
                          trustedContactsState
                        }) => {
  const { name: { accountId } = {} } = account;
  const { firstName, lastName, sequenceNumber } = contact;
  const displayName = `${firstName} ${lastName}`;
  const contactState = trustedContactsState.find(
    (trustedContact) =>
      trustedContact.accountId === accountId &&
      trustedContact.sequenceNumber === sequenceNumber
  );

  return (
    <Fragment>
      <label htmlFor={sequenceNumber}>
        <input
          checked={contactState.active}
          className="trusted-contacts-delete-global-input"
          id={sequenceNumber}
          name={accountId}
          onChange={() => onChange(contactState)}
          type="checkbox"
        />
        <span className="blocked_data">{displayName}</span>
      </label>
    </Fragment>
  );
};

const TrustedContactRow = ({
                             account,
                             onTrustedContactChange,
                             trustedContactsState
                           }) => (
  <div className="trusted-contacts-delete-global-row-container">
    <GroupHeader account={account} />
    <GroupRow
      account={account}
      trustedContactsState={trustedContactsState}
      onChange={onTrustedContactChange}
    />
  </div>
);

class TrustedContactsDeleteGlobal extends React.Component {
  state = {
    trustedContactsState: this.buildTrustedContactsState(),
    showDeleteProcessing: false,
    showModal: false,
  };

  componentDidUpdate(prevProps) {
    const { nonNullTrustedContactMappedGroups, requestStatus } = this.props;
    if (
      isEmpty(prevProps.nonNullTrustedContactMappedGroups) &&
      nonNullTrustedContactMappedGroups !==
      prevProps.nonNullTrustedContactMappedGroups
    ) {
      this.setState({
        trustedContactsState: this.buildTrustedContactsState()
      });
    }

    if (
      prevProps.requestStatus['updateCustomerPreferences'] !==
      requestStatus['updateCustomerPreferences'] &&
      ['RECEIVED', 'ERROR'].includes(requestStatus['updateCustomerPreferences'])
    ) {
      this.resetState();
    }
  }

  handleCancelClick = () => {
    this.resetState();
  };

  handleClearAll = () => {
    const trustedContactsState = cloneDeep(this.state.trustedContactsState).map(
      (contact) => ({
        ...contact,
        active: false
      })
    );
    this.setState({ trustedContactsState });
  };

  handleDeletedTrustedContacts = () => {
    const { customerTrustedContacts, accountsById } = this.props;
    const { trustedContactsState } = this.state;
    let updatedAllTrustedContacts = cloneDeep(customerTrustedContacts);

    const activeContacts = trustedContactsState.filter(({ active }) => active);
    activeContacts.forEach(({ accountId, sequenceNumber }) => {
      const index = updatedAllTrustedContacts.findIndex((contact) => {
        return (
          contact.accountId == accountId &&
          contact.sequenceNumber === sequenceNumber
        );
      });
      if (index > -1) {
        const contact = updatedAllTrustedContacts[index];
        updatedAllTrustedContacts.splice(index, 1, {
          ...contact,
          disposition: { action: 'remove' }
        });
      }
    });
    if(isTrustedContactsAccountNumberEnabled()) {
      updatedAllTrustedContacts = applyAccountNumbersToContacts(updatedAllTrustedContacts, accountsById)
    }
    updateCustomerPreferences({
        trustedContacts: updatedAllTrustedContacts
      }, () => {
        if(clientMissionsEnabled()) {
          try{
            getClientMissions();
          } catch(e) {
            //do nothing
          }
        }
      },
      () => {Store.dispatch(errorUpdateTrustedContacts())});

    this.setState({
      showDeleteProcessing: true
    });
  };

  handleSelectAll = () => {
    const trustedContactsState = cloneDeep(this.state.trustedContactsState).map(
      (contact) => ({
        ...contact,
        active: true
      })
    );
    this.setState({ trustedContactsState });
  };

  onTrustedContactChange = (contactState) => {
    let trustedContactsState = cloneDeep(this.state.trustedContactsState);
    const { accountId, active, sequenceNumber } = contactState;

    const index = trustedContactsState.findIndex(
      (contact) =>
        contact.accountId === accountId &&
        contact.sequenceNumber === sequenceNumber
    );
    if (index > -1) {
      trustedContactsState.splice(index, 1, {
        accountId,
        active: !active,
        sequenceNumber
      });
    }
    this.setState({ trustedContactsState });
  };

  resetState = () => {
    const trustedContactsState = this.buildTrustedContactsState();
    this.setState({
      trustedContactsState,
      showDeleteProcessing: false,
      showModal: false
    });
  };

  showModal = () => {
    this.setState({ showModal: true });
  };

  shouldDisableDelete = () => {
    const { trustedContactsState } = this.state;
    const hasAtLeastOneActiveAccount = trustedContactsState.some(
      ({ active }) => active
    );
    return !hasAtLeastOneActiveAccount;
  };

  buildTrustedContactsState() {
    const { nonNullTrustedContactMappedGroups } = this.props;

    let trustedContactsState = [];
    nonNullTrustedContactMappedGroups.forEach((contact) => {
      const { name: { accountId } = {}, trustedContacts } = contact;
      trustedContacts.forEach(({ sequenceNumber }) =>
        trustedContactsState.push({
          accountId,
          sequenceNumber,
          active: false
        })
      );
    });

    return trustedContactsState;
  }

  render() {
    const { trustedContactsState, showDeleteProcessing, showModal } =
      this.state;

    const { nonNullTrustedContactMappedGroups, linkName, view } = this.props;
    const modalTitle = showDeleteProcessing ? ' ' : this.props.modalTitle;
    const disableDelete = this.shouldDisableDelete();

    return (
      <div className="trusted-contacts-delete-global-container">
        <LinkButton
          className="trusted-contacts-link-button"
          onClick={this.showModal}
          view={view}>
          {linkName}
        </LinkButton>

        {showModal && (
          <BaseModal
            className="trusted-contacts-delete-global-modal"
            disableClickOff={true}
            hideAnchor={true}
            hideClose={true}
            isOpen={showModal}
            title={modalTitle}>
            {!showDeleteProcessing && (
              <Fragment>
                <div className="trusted-contacts-delete-global-contact">
                  <div className="trusted-contacts-delete-global-toggle-all-buttons">
                    <a className="action-link" onClick={this.handleSelectAll}>
                      Select All
                    </a>
                    <a className="action-link" onClick={this.handleClearAll}>
                      Clear All
                    </a>
                  </div>
                  <div className="trusted-contacts-delete-global-content">
                    {nonNullTrustedContactMappedGroups.filter(account=>account.isEditable).map((account, key) => (
                      <TrustedContactRow
                        key={key}
                        account={account}
                        trustedContactsState={trustedContactsState}
                        onTrustedContactChange={this.onTrustedContactChange}
                      />
                    ))}
                  </div>
                </div>
                <div className="trusted-contacts-delete-global-button-group">
                  <button
                    className="button trusted-contacts-delete-global-cancel-button"
                    onClick={this.handleCancelClick}>
                    Cancel
                  </button>
                  <TrustedContactsButtonWithPermissions
                    className="button trusted-contacts-delete-global-delete-button"
                    disabled={disableDelete}
                    onClick={this.handleDeletedTrustedContacts}
                    text="Delete"
                    shouldDisable
                  />
                </div>
              </Fragment>
            )}
            {showDeleteProcessing && (
              <ModalLoadingSpinner label="Deleting Trusted Contacts" />
            )}
          </BaseModal>
        )}
      </div>
    );
  }
}

TrustedContactsDeleteGlobal.propTypes = {
  linkName: string.isRequired,
  modalTitle: string.isRequired
};

const mapStateToProps = (state) => ({
  customerTrustedContacts: getCustomerTrustedContacts(state),
  nonNullTrustedContactMappedGroups:
    getNonNullTrustedContactMappedGroups(state),
  requestStatus: state.requestStatus,
  view: getView(state),
  accountsById: getAccountsById(state)
});

export default connect(mapStateToProps)(TrustedContactsDeleteGlobal);
